/**
 * The catalogue of wines as a table (P1-4): a template to fill in, the catalogue to take away,
 * and a plan of what an uploaded table would change. Nothing is written here; the app shows the
 * plan and books it only after the user agreed.
 *
 * Rules:
 *   - A row is a wine in one vintage and bottle size. It is recognised by producer, name, vintage
 *     and size, so the same table can be uploaded again: known wines are updated, not doubled.
 *     A workspace without vintages (F23) recognises a wine without its vintage and passes the
 *     vintage over, except for wines kept by vintage already.
 *   - Stock comes from the table only as opening stock, for wines without stock yet. After that
 *     only bookings change it; otherwise journal and inventory would no longer agree.
 *   - A row is left out only for a problem in what makes the wine (producer, name, colour, vintage,
 *     size); a problem in any other cell leaves that cell out, with a note.
 *   - The same wine on several rows is one wine; the stock of each row goes to its own place.
 *   - Colours and places the import does not know are decided once per value: a colour is mapped to
 *     one of the profile's, a place to a known one or created. The decisions are remembered
 *     (packages/core/src/import-record.ts).
 *   - An empty colour leaves a known wine's colour as it is and takes the colour of another row of
 *     the same wine; the other rows are grouped by the colour their name points to ("Sekt" →
 *     Schaumwein) and decided once per group.
 */
import { BOTTLE_SIZES, WINE_COLORS, WINE_FIELDS, WINE_PROFILE as WINE_PROFILE_, isProfiled, tasteOf } from "./profile-wine.ts";
import type { ProfileField } from "./profile-wine.ts";
import { formatGlasses, parseGlasses } from "./glass.ts";
import { CONTAINERS, allergenIds, allergenNames, formatAlcohol, parseAlcohol, profileOf, sizeOf } from "./drinks.ts";
import { ALL_KINDS, PROFILES, PROFILE_TITLES, drinkName, kindProfile, profileById } from "./profiles.ts";
import { containerFor, defaultPacks } from "./profile.ts";
import type { Profile } from "./profile.ts";
import type { Receipt } from "./model.ts";
import { slug } from "./ids.ts";
import { distance, fold } from "./search.ts";
import type { GlassPrice } from "./glass.ts";
import { DEFAULT_RUBRICS, RECIPE_KINDS } from "./menu.ts";
import type { Recipe, Rubric } from "./menu.ts";

export type Cell = string | number | boolean | Date | null | undefined;

/** Columns besides the profile fields. */
const EXTRA = [
  { key: "barcode", label: "Strichcode" },
  { key: "stock", label: "Bestand (Flaschen)" },
  { key: "location", label: "Lagerort" },
  { key: "unitPrice", label: "Einkaufspreis je Flasche" },
  { key: "rubric", label: "Rubrik" },
] as const;

export interface Column {
  key: string;
  label: string;
}

/** The columns of the template, in this order. A range field becomes two columns. */
export const IMPORT_COLUMNS: readonly Column[] = [
  ...["listNo", "producer", "wine", "color", "vintage", "size", "country", "region", "appellation", "grapes", "salePrice", "cardText", "byGlass", "offCard", "taste", "organic", "alcohol", "closure"].map((key) => {
    const f = WINE_FIELDS.find((x) => x.key === key)!;
    return { key, label: f.label };
  }),
  { key: "drinkFrom", label: "Trinkfenster von" },
  { key: "drinkTo", label: "Trinkfenster bis" },
  { key: "rating", label: "Bewertung" },
  { key: "note", label: "Notiz" },
  ...EXTRA,
  // Analysis and label (18.10.2026), at the end, so older tables and the examples keep their order.
  ...["quality", "sugar", "acidity", "ph", "energy", "ingredients", "bottler", "eLabel"].map((key) => ({ key, label: WINE_FIELDS.find((x) => x.key === key)!.label })),
  // For the kitchen (18.10.2026), likewise at the end.
  { key: "cooking", label: WINE_FIELDS.find((x) => x.key === "cooking")!.label },
];

/** Columns of the other profiles' sheets (docs/17-getraenke.md, Import und Export), in this order. */
const OTHER_COLUMNS: Readonly<Record<string, readonly string[]>> = {
  spirituosen: ["listNo", "producer", "wine", "color", "age", "size", "country", "region", "salePrice", "cardText", "byGlass", "offCard", "organic", "alcohol", "additives", "allergens", "note"],
  bier: ["listNo", "producer", "wine", "color", "size", "container", "country", "region", "salePrice", "cardText", "byGlass", "offCard", "organic", "alcohol", "deposit", "additives", "allergens", "note"],
  alkoholfrei: ["listNo", "producer", "wine", "color", "sparkling", "size", "container", "country", "region", "salePrice", "cardText", "byGlass", "offCard", "organic", "alcohol", "deposit", "additives", "allergens", "note"],
  weitere: ["listNo", "producer", "wine", "color", "size", "container", "country", "region", "salePrice", "cardText", "byGlass", "offCard", "taste", "organic", "alcohol", "deposit", "additives", "allergens", "note"],
};

/** The columns of a profile's sheet: the wine's as IMPORT_COLUMNS, the others by their fields. */
export function importColumns(profileId: string): readonly Column[] {
  const keys = OTHER_COLUMNS[profileId];
  if (!keys) return IMPORT_COLUMNS;
  const profile = profileById(profileId);
  return [...keys.map((key) => ({ key, label: profile.fields.find((f) => f.key === key)!.label })), ...EXTRA.map((c) => (c.key === "unitPrice" ? { ...c, label: "Einkaufspreis je Gebinde" } : c))];
}

/** Every column any sheet has, for recognising headings. */
const ALL_COLUMNS: readonly Column[] = [...IMPORT_COLUMNS, ...PROFILES.flatMap((p) => importColumns(p.id)).filter((c) => !IMPORT_COLUMNS.some((w) => w.key === c.key && w.label === c.label))];

/** Other headings people use for the same column. */
const ALIASES: Record<string, string[]> = {
  listNo: ["nr", "nummer", "kartennr", "binnummer", "bin", "position", "pos"],
  producer: ["weingut", "winzer", "produzent", "hersteller", "marke", "brauerei", "brennerei", "destillerie"],
  wine: ["wein", "bezeichnung", "cuvee", "weinname"],
  color: ["art", "typ", "kategorie", "weinfarbe"],
  vintage: ["jahr", "jg"],
  size: ["flasche", "groesse", "inhalt", "liter", "volumen"],
  grapes: ["rebsorte", "traube", "trauben", "sorte"],
  salePrice: ["verkaufspreis", "vk", "kartenpreis", "preis"],
  unitPrice: ["einkaufspreis", "ek"],
  stock: ["bestand", "menge", "anzahl", "flaschen"],
  location: ["lagerort", "ort", "lager", "regal"],
  barcode: ["ean", "gtin", "strichcode", "barcode"],
  alcohol: ["alkohol", "vol"],
  cardText: ["beschreibung", "beschreibungstext", "weinbeschreibung", "kartenbeschreibung", "description"],
  offCard: ["nichtaufderkarte", "nichtaufkarte", "ausgeblendet", "verborgen"],
  byGlass: ["glaspreise", "glaspreis", "offen", "offenerwein", "ausschank", "glas"],
  age: ["alter", "reifung"],
  container: ["gebinde", "behaelter", "verpackungsart"],
  deposit: ["pfand"],
  additives: ["zusatzstoffe", "zusatzstoff"],
  allergens: ["allergene", "allergen"],
  sparkling: ["kohlensaeure"],
  rubric: ["rubrik", "kartenrubrik"],
  quality: ["qualitaet", "qualitaetsstufe", "praedikat", "klassifikation"],
  sugar: ["restzucker", "zucker", "restsuesse"],
  acidity: ["saeure", "gesamtsaeure"],
  ph: ["phwert"],
  energy: ["brennwert", "kalorien", "kcal", "energie"],
  ingredients: ["zutaten", "zutatenliste"],
  bottler: ["abfueller"],
  eLabel: ["elabel", "qrcode", "elabellink"],
  cooking: ["kochwein", "kochen", "fuerdiekueche"],
};

const norm = (s: string) =>
  s.toLowerCase().replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss").replace(/é/g, "e").replace(/[^a-z0-9]/g, "");

/** Which column a heading stands for, or null. */
export function columnOf(heading: string): string | null {
  const h = norm(heading);
  if (h === "") return null;
  for (const c of ALL_COLUMNS) if (norm(c.label) === h || norm(c.key) === h) return c.key;
  for (const [key, names] of Object.entries(ALIASES)) if (names.includes(h)) return key;
  // "Preis in €", "Menge Flaschen": a known word at the start.
  for (const [key, names] of Object.entries(ALIASES)) if (names.some((n) => n.length >= 3 && h.startsWith(n))) return key;
  return null;
}

const COLOR_ALIASES: Record<string, string> = {
  rot: "Rot", rotwein: "Rot", red: "Rot", rosso: "Rot", rouge: "Rot", tinto: "Rot",
  weiss: "Weiß", weisswein: "Weiß", white: "Weiß", bianco: "Weiß", blanc: "Weiß", blanco: "Weiß",
  rose: "Rosé", rosewein: "Rosé", rosato: "Rosé",
  schaumwein: "Schaumwein", sekt: "Schaumwein", champagner: "Schaumwein", champagne: "Schaumwein", prosecco: "Schaumwein", cava: "Schaumwein", cremant: "Schaumwein", spumante: "Schaumwein", sparkling: "Schaumwein",
  suesswein: "Süßwein", suess: "Süßwein", dessertwein: "Süßwein", dessert: "Süßwein", sweet: "Süßwein",
  likoerwein: "Likörwein", verstaerkt: "Likörwein", port: "Likörwein", portwein: "Likörwein", sherry: "Likörwein", madeira: "Likörwein", marsala: "Likörwein",
  orangewein: "Orange", orangewine: "Orange", maischevergoren: "Orange", amber: "Orange", amberwine: "Orange",
};

/** Words for the kinds of the other profiles (docs/17-getraenke.md): „Wodka“ is a spirit, „Pils“ a beer. */
const KIND_ALIASES: Record<string, string> = {
  gin: "Gin", rum: "Rum", whisky: "Whisky", whiskey: "Whisky", bourbon: "Whisky", scotch: "Whisky", wodka: "Wodka", vodka: "Wodka",
  brand: "Brand und Obstbrand", obstbrand: "Brand und Obstbrand", obstler: "Brand und Obstbrand", schnaps: "Brand und Obstbrand", geist: "Brand und Obstbrand", brandy: "Brand und Obstbrand", cognac: "Brand und Obstbrand", calvados: "Brand und Obstbrand", armagnac: "Brand und Obstbrand",
  grappa: "Grappa", tresterbrand: "Grappa", likoer: "Likör", liqueur: "Likör", bitter: "Bitter und Aperitif", aperitif: "Bitter und Aperitif", amaro: "Bitter und Aperitif", spirituose: "Sonstige Spirituose", spirituosen: "Sonstige Spirituose",
  pils: "Pils", pilsener: "Pils", pilsner: "Pils", helles: "Helles", hell: "Helles", export: "Export", maerzen: "Märzen und Festbier", festbier: "Märzen und Festbier",
  weizen: "Weizen", weizenbier: "Weizen", weissbier: "Weizen", hefeweizen: "Weizen", koelsch: "Kölsch", alt: "Alt", altbier: "Alt", dunkel: "Dunkel", dunkles: "Dunkel",
  bock: "Bock", doppelbock: "Bock", kellerbier: "Keller- und Zwickelbier", zwickel: "Keller- und Zwickelbier", ipa: "Pale Ale und IPA", paleale: "Pale Ale und IPA",
  stout: "Stout und Porter", porter: "Stout und Porter", radler: "Radler und Biermischgetränk", alsterwasser: "Radler und Biermischgetränk", biermischgetraenk: "Radler und Biermischgetränk", bier: "Sonstiges Bier",
  wasser: "Wasser", mineralwasser: "Wasser", limonade: "Limonade und Cola", limo: "Limonade und Cola", cola: "Limonade und Cola", saft: "Saft und Nektar", nektar: "Saft und Nektar",
  schorle: "Schorle", eistee: "Eistee", energy: "Energy", energydrink: "Energy", tonic: "Tonic und Mixer", mixer: "Tonic und Mixer", gingerale: "Tonic und Mixer", sirup: "Sirup",
  kombucha: "Kombucha und Fermentiertes", alkoholfrei: "Sonstiges Alkoholfreies", softdrink: "Limonade und Cola",
  cidre: "Cidre und Cider", cider: "Cidre und Cider", apfelwein: "Cidre und Cider", viez: "Cidre und Cider", obstwein: "Obst- und Fruchtwein", fruchtwein: "Obst- und Fruchtwein",
  met: "Met", honigwein: "Met", sake: "Sake", hardseltzer: "Hard Seltzer", seltzer: "Hard Seltzer", mischgetraenk: "Fertige Mischgetränke", alkopop: "Fertige Mischgetränke",
};

/** Words that stand near a colour without being quite it: proposed in the preview, the user decides. */
const COLOR_SUGGESTIONS: Record<string, string> = {
  weissherbst: "Rosé", rotling: "Rosé", schiller: "Rosé", schillerwein: "Rosé", badischrotgold: "Rosé", blush: "Rosé", clairet: "Rosé",
  blancdenoirs: "Weiß", blancdenoir: "Weiß",
  perlwein: "Schaumwein", secco: "Schaumwein", frizzante: "Schaumwein", petnat: "Schaumwein", petillantnaturel: "Schaumwein",
  eiswein: "Süßwein", beerenauslese: "Süßwein", trockenbeerenauslese: "Süßwein", tba: "Süßwein", ausbruch: "Süßwein", strohwein: "Süßwein",
  sauternes: "Süßwein", tokajer: "Süßwein", tokaji: "Süßwein", vinsanto: "Süßwein", passito: "Süßwein",
};

const text = (v: Cell) => (v === null || v === undefined ? "" : v instanceof Date ? v.toISOString().slice(0, 10) : String(v).trim());

function number(v: Cell): number | null {
  if (typeof v === "number") return v;
  // „20,-“ and „6 Fl.“ are numbers too.
  const t = text(v)
    .replace(/\s|€|%|vol\.?/gi, "")
    .replace(/[,.][-–]+$/, "")
    .replace(/(?<=\d)(eur|euro|fl|flaschen?|stk|stück|stueck)\.?$/i, "");
  if (t === "") return null;
  // "1.234,50" and "1234.50" both mean the same.
  const n = Number(/,\d{1,2}$/.test(t) ? t.replace(/\./g, "").replace(",", ".") : t.replace(/,/g, ""));
  return Number.isFinite(n) ? n : NaN;
}

/**
 * A size cell in ml, as the candidates it may mean: „0,75“, „75 cl“, „750 ml“, „30 l“, „Magnum“.
 * A number without unit above 10 is tried as cl, as ml, then as litres („30“: a keg of 30 l); in a
 * keg as litres first („50“: 50 l, not 0,5 l).
 */
function sizeMl(raw: Cell, t: string, bulk = false): number[] {
  if (/jeroboam|doppelmagnum/i.test(t)) return [3000];
  if (/magnum/i.test(t)) return [1500];
  const n = typeof raw === "number" ? raw : number(t.replace(/[a-z]/gi, ""));
  if (n === null || !(n > 0)) return [];
  if (/cl/i.test(t)) return [n * 10];
  if (/ml/i.test(t)) return [n];
  if (n <= 10 || /\d\s*l\b|liter/i.test(t)) return [n * 1000];
  return bulk ? [n * 1000, n * 10, n] : [n * 10, n, n * 1000];
}

/** The size a row gets when its cell is empty. */
const DEFAULT_SIZE: Readonly<Record<string, number>> = { wein: 750, spirituosen: 700, bier: 500, alkoholfrei: 1000, weitere: 750 };

/** Other words for a container: „Fässer“, „Keg“, „BiB“. */
const CONTAINER_ALIASES: Readonly<Record<string, string>> = { keg: "Fass", kegs: "Fass", fassbier: "Fass", bib: "Bag-in-Box", bagbox: "Bag-in-Box", fl: "Flasche", flaschen: "Flasche" };

/** A cell as the value stored in the field of `profile`; `undefined` for empty. The container of the row reads its size („50“ in a keg). */
function valueOf(key: string, raw: Cell, profile: Profile = WINE_PROFILE_, container?: unknown): { value?: unknown; error?: string } {
  const t = text(raw);
  if (t === "") return {};
  const field: ProfileField | undefined = profile.fields.find((f) => f.key === key);
  switch (key) {
    case "vintage": {
      if (/^(o\.?\s*j\.?|nv|n\.v\.|ohne jahrgang|-)$/i.test(t)) return {};
      const n = typeof raw === "number" ? raw : raw instanceof Date ? raw.getFullYear() : parseInt(t, 10);
      return Number.isInteger(n) && n >= 1800 && n <= 2100 ? { value: n } : { error: `Jahrgang „${t}“ ist kein Jahr` };
    }
    case "drinkFrom":
    case "drinkTo": {
      const n = typeof raw === "number" ? raw : parseInt(t, 10);
      return Number.isInteger(n) && n >= 1800 && n <= 2200 ? { value: n } : { error: `Trinkfenster „${t}“ ist kein Jahr` };
    }
    case "size": {
      const sizes = field?.sizes ?? BOTTLE_SIZES;
      const found = sizeMl(raw, t, profile.bulk !== undefined && container === profile.bulk.container)
        .map((ml) => sizes.find((s) => Math.abs(s - ml) < 1))
        .find((s) => s !== undefined);
      return found ? { value: found } : { error: `${field?.label ?? "Größe"} „${t}“ unbekannt (z. B. ${String((DEFAULT_SIZE[profile.id] ?? 750) / 1000).replace(".", ",")})` };
    }
    case "age":
      return { value: typeof raw === "number" ? String(raw) : t };
    case "container": {
      // Also the plural and other words: „Fässer“, „Dosen“, „Keg“.
      const k = norm(t);
      const found = field?.options?.find((o) => norm(o) === k || norm(CONTAINERS[o]?.plural ?? "") === k || norm(CONTAINER_ALIASES[k] ?? "") === norm(o));
      return found ? { value: found } : { error: `${field?.label ?? "Gebinde"} „${t}“ unbekannt` };
    }
    case "additives":
    case "allergens": {
      const words = t.split(/[,;/]/).map((s) => s.trim()).filter(Boolean);
      const known = words.map((w) => field?.options?.find((o) => norm(o) === norm(w) || norm(o).includes(norm(w))));
      if (!known.every(Boolean)) return { error: `${field?.label ?? key} „${t}“ unbekannt` };
      // Allergens are stored by their ids (household-kit food), additives by their words.
      return { value: key === "allergens" ? allergenIds(known) : [...new Set(known as string[])] };
    }
    case "deposit":
    case "salePrice":
    case "unitPrice": {
      const n = number(raw);
      return n !== null && n >= 0 ? { value: Math.round(n * 100) } : { error: `${key === "salePrice" ? "Verkaufspreis" : key === "deposit" ? "Pfand" : "Einkaufspreis"} „${t}“ ist kein Betrag` };
    }
    case "alcohol":
      // A number, a cell formatted as percent (0.135), or an upper bound („< 0,5“).
      return parseAlcohol(typeof raw === "number" ? raw : t);
    case "stock": {
      const n = number(raw);
      return n !== null && Number.isInteger(n) && n >= 0 ? { value: n } : { error: `Bestand „${t}“ ist keine ganze Zahl` };
    }
    case "offCard":
      // Yes hides the wine; no puts it back on the card; empty leaves it as it is.
      if (/^(ja|j|x|yes|y|true|wahr|1)$/i.test(t)) return { value: true };
      if (/^(nein|n|no|false|falsch|0|-)$/i.test(t)) return { value: null };
      return { error: `Nicht auf der Karte „${t}“: bitte „ja“ oder „nein“` };
    case "organic":
      return { value: /^(ja|j|x|yes|y|true|wahr|1|bio)$/i.test(t) };
    case "cooking":
      // Like „Nicht auf der Karte“: yes marks it, no takes the mark away, empty leaves it as it is.
      if (/^(ja|j|x|yes|y|true|wahr|1)$/i.test(t)) return { value: true };
      if (/^(nein|n|no|false|falsch|0|-)$/i.test(t)) return { value: null };
      return { error: `Zum Kochen „${t}“: bitte „ja“ oder „nein“` };
    case "grapes":
      return { value: t.split(/[,;/]/).map((s) => s.trim()).filter(Boolean) };
    case "barcode": {
      const codes = t.split(/[\s,;]+/).map((c) => c.replace(/\.0$/, "")).filter(Boolean);
      return codes.every((c) => /^\d{8,14}$/.test(c)) ? { value: codes } : { error: `Strichcode „${t}“: 8 bis 14 Ziffern` };
    }
    case "byGlass":
      return parseGlasses(t);
    case "listNo":
      return { value: typeof raw === "number" ? String(raw) : t };
    case "rubric":
      return { value: t };
  }
  if (field?.type === "number") {
    const n = number(raw);
    const max = key === "ph" ? 5 : key === "energy" ? 1000 : 500;
    return n !== null && Number.isFinite(n) && n >= 0 && n <= max ? { value: n } : { error: `${field.label} „${t}“ ist keine Zahl` };
  }
  if (field?.type === "choice" && field.options) {
    const found = field.options.find((o) => norm(o) === norm(t));
    return found ? { value: found } : { error: `${field.label} „${t}“ unbekannt` };
  }
  return { value: t };
}

export interface ExistingArticle {
  id: string;
  fields: Record<string, unknown>;
  /** Containers (bottles) in stock anywhere, not ml; opening stock only goes to wines with none. */
  stock: number;
}

export interface Place {
  id: string;
  /** Id of the storage. */
  lager: string;
  /** "Weinkeller · Regal A" */
  label: string;
  lagerName: string;
  name: string;
}

export interface PlannedStock {
  /** The row of the table it comes from. */
  line: number;
  /** Bottles, as in the table (the booking is in ml: `plannedReceipt`). */
  qty: number;
  /** ml per bottle: the wine's size. */
  ml: number;
  lager: string;
  location: string;
  /** Cents per bottle, or null. */
  unitPrice: number | null;
}

export interface PlannedRow {
  /** The first row of the wine. */
  line: number;
  /** All its rows: the same wine may stand on several. */
  lines: number[];
  /** The sheet, in a workbook with a sheet per profile. */
  sheet?: string;
  action: "create" | "update" | "same";
  id: string;
  name: string;
  /** All fields for a new wine; only the changed ones for a known wine. */
  fields: Record<string, unknown>;
  /** Opening stock, one entry per row with stock. */
  stock: PlannedStock[];
}

export interface ImportNote {
  line: number;
  message: string;
  /** The sheet, in a workbook with a sheet per profile. */
  sheet?: string;
}

/** Where a decision comes from: made in this preview, remembered from an earlier import, proposed by the import, or none yet. */
export type ChoiceSource = "chosen" | "remembered" | "suggested" | "none";

/** A colour word the import does not know; all its rows take the colour chosen, or are left out. */
export interface UnknownColor {
  /** As first written in the table; empty for rows without a colour. */
  value: string;
  key: string;
  lines: number[];
  color: string | null;
  from: ChoiceSource;
  /** Rows without a colour, grouped by the colour their name points to (null: no hint). */
  empty?: { guess: string | null; names: string[] };
}

/** What happens with a place the import does not know. */
export type PlaceChoice =
  | { kind: "place"; id: string }
  | { kind: "new"; lager: string; name: string }
  /** A new storage by this name, with the location in it. */
  | { kind: "newLager"; lager: string; name: string }
  | { kind: "skip" };

export interface PlaceTarget {
  lager: string;
  location: string;
  label: string;
  /** Created by this import. */
  created: boolean;
}

export interface UnknownPlace {
  value: string;
  key: string;
  lines: number[];
  /** Bottles in the rows with this place. */
  bottles: number;
  /** The place as storage and location, if written as „Lager · Lagerort“. */
  parts: { lager: string | null; name: string };
  choice: PlaceChoice;
  from: ChoiceSource;
  /** Where the stock goes; null leaves it out. */
  target: PlaceTarget | null;
}

/** Decisions for unknown values, by their key: made in the preview, or remembered from earlier imports. */
export interface ImportChoices {
  /** A colour, or null to leave the rows out. */
  colors: Record<string, string | null>;
  places: Record<string, PlaceChoice>;
}

export interface ImportOptions {
  /** The storages new locations may go to (those of the device); by default those of `places`. */
  lagers?: { id: string; name: string }[];
  /** Ids of storages and locations in use, so new ones get their own. */
  taken?: Iterable<string>;
  choices?: Partial<ImportChoices>;
  remembered?: Partial<ImportChoices>;
  /** The card's rubrics, for the column „Rubrik“; default Aperitif, Longdrinks und Cocktails, Digestif. */
  rubrics?: readonly Rubric[];
  /** The prepared drinks there are, so the sheet „Zubereitet“ updates them instead of doubling them. */
  recipes?: readonly Recipe[];
  /**
   * false: the workspace keeps no vintages (F23). A wine is recognised by producer, name and size, a
   * vintage cell is passed over with a note; only wines that have a vintage already keep theirs.
   */
  vintages?: boolean;
}

/** A prepared drink of the sheet „Zubereitet“ (Spritz, Gin Tonic, Apfelschorle): recognised by its name. */
export interface PlannedRecipe {
  line: number;
  sheet?: string;
  action: "create" | "update" | "same";
  id: string;
  name: string;
  /** All fields for a new one; only the changed ones for a known one. */
  fields: Record<string, unknown>;
}

export interface ImportPlan {
  rows: PlannedRow[];
  /** Rows left out, with the reason. */
  errors: ImportNote[];
  /** Rows taken, with something left out or worth knowing. */
  notes: ImportNote[];
  /** Headings the import did not understand; their columns are ignored. */
  ignored: string[];
  colors: UnknownColor[];
  places: UnknownPlace[];
  /** Storages and locations to create before the stock is booked. */
  newLagers: { id: string; name: string }[];
  newPlaces: { id: string; lager: string; name: string; label: string }[];
  /** Prepared drinks of the sheet „Zubereitet“. */
  recipes: PlannedRecipe[];
}

/** The note of an import's opening stock: the id of its record (B6), as an inventory's cause carries the inventory. */
export const importNote = (id: string) => `import:${id}`;

/** The import an opening stock came from, or null. */
export const importOfNote = (note: string | undefined): string | null => (note?.startsWith("import:") ? note.slice(7) : null);

/**
 * The receipt of a planned stock line: bottles times their size in ml, the price per bottle as the
 * amount; with the import's id as note (`importNote`).
 */
export function plannedReceipt(r: PlannedRow, s: PlannedStock, from?: string): Omit<Receipt, "id" | "at" | "recordedAt"> {
  return { kind: "in", article: r.id, lager: s.lager, location: s.location, qty: s.qty * s.ml, reason: "opening", ...(s.unitPrice !== null ? { amount: s.unitPrice * s.qty } : {}), ...(from ? { note: importNote(from) } : {}) };
}

/** Bottles of opening stock in a planned row. */
export const plannedBottles = (r: PlannedRow) => r.stock.reduce((s, x) => s + x.qty, 0);

/** What makes a drink this drink: producer, name, vintage or age, size, container (a beer of 30 l without one: Fass). */
const keyOf = (f: Record<string, unknown>) => {
  const ml = sizeOf(f) ?? DEFAULT_SIZE[profileOf(f)] ?? 750;
  const container = containerFor(profileById(profileOf(f)), ml, f.container) ?? "";
  return [f.producer, f.wine ?? "", typeof f.vintage === "number" ? f.vintage : (f.age ?? ""), ml, container === "Flasche" ? "" : container].map((v) => norm(String(v ?? ""))).join("|");
};
const familyKey = (f: Record<string, unknown>) => `${profileOf(f)}|${norm(String(f.producer ?? ""))}|${norm(String(f.wine ?? ""))}`;

/** What makes a wine a wine: a problem here leaves the row out. A problem in any other column only leaves that cell out. */
const IDENTITY = new Set(["producer", "wine", "color", "vintage", "size", "age", "container"]);
const labelOf = (key: string) => IMPORT_COLUMNS.find((c) => c.key === key)?.label ?? key;

/** A colour or kind as the import knows it without asking, or undefined. */
const knownColor = (t: string): string | undefined => ALL_KINDS.find((c) => norm(c) === norm(t)) ?? COLOR_ALIASES[norm(t)] ?? KIND_ALIASES[norm(t)];

/** A colour to propose for a word the import does not know: „Weißherbst“ → Rosé, „Rotwein trocken“ → Rot. */
export function suggestColor(t: string): string | null {
  const whole = norm(t);
  if (COLOR_SUGGESTIONS[whole]) return COLOR_SUGGESTIONS[whole];
  for (const word of fold(t).split(" ").map(norm)) {
    const found = knownColor(word) ?? COLOR_SUGGESTIONS[word];
    if (found) return found;
  }
  return null;
}

/** Words in producer and name that point to a colour, strongest first: a Rosé Sekt is a Schaumwein. */
const NAME_HINTS: [string, RegExp][] = [
  ["Schaumwein", /\b(sekt\w*|\w*secco|prosecco|cava|champagner?|cremant|spumante|frizz\w*|perl\w*|brut|petillant|petnat|prickel\w*|schaumwein|lambrusco|asti)\b/],
  ["Likörwein", /\b(sherry|port|portwein|porto|portonic|madeira|marsala|vermut|vermouth|wermut|lillet|likoerwein)\b/],
  ["Süßwein", /\b(eiswein|\w*beerenauslese|tba|sauternes|tokaji|tokajer|dessertwein|dessert|vin santo|vinsanto|passito|strohwein|suesswein)\b/],
  ["Orange", /\b(orangewein|orange wine|maischevergoren\w*|amber)\b/],
  ["Likör", /\b(\w*likoer|limoncello|amaretto|baileys|sambuca|ouzo|pastis)\b/],
  ["Bitter und Aperitif", /\b(aperol|campari|ramazzotti|amaro|averna|cynar|jaegermeister|bitter)\b/],
  ["Grappa", /\b(grappa|tresterbrand)\b/],
  ["Gin", /\b(gin)\b/],
  ["Rum", /\b(rum|rhum)\b/],
  ["Whisky", /\b(whisky|whiskey|bourbon|scotch|single malt)\b/],
  ["Wodka", /\b(wodka|vodka)\b/],
  ["Brand und Obstbrand", /\b(\w*brand|obstler|\w*geist|schnaps|cognac|calvados|armagnac|brandy|kirschwasser|zwetschgenwasser)\b/],
  ["Cidre und Cider", /\b(cidre|cider|apfelwein|viez|sidra)\b/],
  ["Met", /\b(met|honigwein)\b/],
  ["Sake", /\b(sake)\b/],
  ["Hard Seltzer", /\b(seltzer)\b/],
  ["Radler und Biermischgetränk", /\b(radler|alsterwasser)\b/],
  ["Weizen", /\b(\w*weizen|weissbier)\b/],
  ["Pils", /\b(pils|pilsener|pilsner)\b/],
  ["Sonstiges Bier", /\b(\w*bier|lager|ipa|stout|porter|koelsch|bock)\b/],
  ["Wasser", /\b(\w*wasser|sprudel)\b/],
  ["Saft und Nektar", /\b(\w*saft|nektar)\b/],
  ["Schorle", /\b(\w*schorle)\b/],
  ["Limonade und Cola", /\b(cola|limo\w*|fanta|sprite|brause)\b/],
  ["Tonic und Mixer", /\b(tonic|ginger ale|bitter lemon)\b/],
  ["Fertige Mischgetränke", /\b(mischgetraenk|alkopop)\b/],
  ["Rosé", /\b(rose|rosewein|rosato|rosado|weissherbst|rotling|schiller\w*|blush|clairet)\b/],
  ["Weiß", /\b(weisswein|weiss|blanc de noirs?|blanc|bianco|blanco)\b/],
  ["Rot", /\b(rotwein|rot|rosso|rouge|tinto)\b/],
];
const RED_GRAPES =
  /\b(spaetburgunder|pinot noir|merlot|cabernet|syrah|shiraz|sangiovese|nebbiolo|barbera|tempranillo|grenache|garnacha|primitivo|zinfandel|zweigelt|blaufraenkisch|lemberger|dornfelder|trollinger|portugieser|malbec|montepulciano|lagrein|monastrell|dolcetto|pinotage|gamay|chianti|barolo|rioja|amarone|valpolicella)\b/;
const WHITE_GRAPES =
  /\b(riesling|grauburgunder|weissburgunder|chardonnay|sauvignon blanc|silvaner|sylvaner|mueller thurgau|gruener veltliner|gewuerztraminer|pinot grigio|pinot gris|pinot blanc|kerner|auxerrois|muskateller|scheurebe|albarino|verdejo|viognier|chenin blanc|chablis|soave|gavi)\b/;

/**
 * The colour (or kind of another profile) a producer and name point to, for a row without one:
 * „Riesling Sekt“ → Schaumwein, „Pedro Ximénez Sherry“ → Likörwein, „Merlot“ → Rot, „Limoncello“ →
 * Likör, „Cidre“ → Cidre und Cider. Null when nothing points to one, or red and white grapes both do.
 */
export function colorFromName(text: string): string | null {
  const t = fold(text);
  for (const [color, re] of NAME_HINTS) if (re.test(t)) return color;
  const red = RED_GRAPES.test(t);
  const white = WHITE_GRAPES.test(t);
  return red === white ? null : red ? "Rot" : "Weiß";
}

/** „Weinkeller · Regal A“, „Keller / Regal 3“, „Garage, Kiste 2“, „Weinkeller . Karton“: storage and location. */
function splitPlace(value: string): { lager: string | null; name: string } {
  const parts = value.split(/\s*[·|>,/]\s*|\s+[-–.]\s+/).map((s) => s.trim()).filter(Boolean);
  const cut = (s: string) => s.slice(0, 40);
  return parts.length >= 2 ? { lager: cut(parts[0]!), name: cut(parts.slice(1).join(" ")) } : { lager: null, name: cut(value.trim()) };
}

/**
 * A known place spelled a little differently: „Regla A“ for „Regal A“. Numbers and short words
 * must be the same, so „Regal C“ is not taken for „Regal A“.
 */
function similarPlace(value: string, places: Place[]): Place | undefined {
  const words = fold(value).split(" ");
  const close = (other: string) => {
    const theirs = fold(other).split(" ");
    return (
      theirs.length === words.length &&
      words.some((w, i) => w !== theirs[i]) &&
      words.every((w, i) => {
        const t = theirs[i]!;
        if (w === t) return true;
        if (/\d/.test(w) || w.length <= 3) return false;
        const max = w.length <= 6 ? 1 : 2;
        return distance(w, t, max) <= max;
      })
    );
  };
  return places.find((p) => close(p.name) || close(`${p.lagerName} ${p.name}`));
}

/** A sheet of a workbook: its name says the profile („Bier“, „Spirituosen“); unknown or empty: Wein. */
export interface ImportSheet {
  name: string;
  rows: Cell[][];
}

/** The profile a sheet's name stands for: „Weine“, „Spirituosen“, „Biere“, „Alkoholfrei“, „Weitere Getränke“. */
export function sheetProfile(name: string): string | undefined {
  const n = norm(name);
  if (n === "") return undefined;
  for (const p of PROFILES) if (norm(PROFILE_TITLES[p.id] ?? "") === n || norm(p.vocabulary.articles) === n || norm(p.id) === n) return p.id;
  if (/wein/.test(n)) return "wein";
  if (/spirit|schnaps|likoer/.test(n)) return "spirituosen";
  if (/bier/.test(n)) return "bier";
  if (/alkoholfrei|wasser|saft|soft/.test(n)) return "alkoholfrei";
  if (/weitere|cidre|cider/.test(n)) return "weitere";
  return undefined;
}

/**
 * What the table would change. `sheet` is the sheet as read; the headings are found in the first
 * rows. `newId` makes ids for new wines and families. Unknown colours and places are collected
 * with a decision each (`plan.colors`, `plan.places`); a new plan with `options.choices` applies
 * other decisions.
 */
export function planImport(sheet: Cell[][], existing: ExistingArticle[], places: Place[], newId: () => string, options: ImportOptions = {}): ImportPlan {
  return planWorkbook([{ name: "", rows: sheet }], existing, places, newId, options);
}

/**
 * The same for a workbook with a sheet per profile (docs/17-getraenke.md, Import und Export). A row
 * belongs to the profile of its kind („Gin“: Spirituosen), else to its sheet's; a row without a
 * kind is decided like a wine without a colour, with all kinds of all profiles to choose from.
 * Sheets without headings (the guide) are passed over.
 */
export function planWorkbook(sheets: ImportSheet[], existing: ExistingArticle[], places: Place[], newId: () => string, options: ImportOptions = {}): ImportPlan {
  const plan: ImportPlan = { rows: [], errors: [], notes: [], ignored: [], colors: [], places: [], newLagers: [], newPlaces: [], recipes: [] };
  const many = sheets.length > 1;
  const rubrics = options.rubrics ?? DEFAULT_RUBRICS;
  const recipeSheets = sheets.filter((sh) => isRecipeSheet(sh.name));
  const read = sheets
    .filter((sh) => !isRecipeSheet(sh.name))
    .map((sh) => ({ ...sh, headerAt: sh.rows.findIndex((row) => row.filter((c) => columnOf(text(c)) !== null).length >= 2) }))
    .filter((sh, i) => sh.headerAt >= 0 || (!many && i === 0));
  if ((read.length === 0 || read.every((sh) => sh.headerAt < 0)) && !recipeSheets.length) {
    plan.errors.push({ line: 1, message: "Keine Überschriften gefunden. Die erste Zeile braucht mindestens „Erzeuger“ und „Name“; am einfachsten mit der Vorlage." });
    return plan;
  }
  const at = (sheet: string) => (many ? { sheet } : {});

  const known = new Map<string, ExistingArticle>();
  const families = new Map<string, string>();
  for (const a of existing) {
    if (!isProfiled(a.fields) && typeof a.fields.producer !== "string") continue;
    known.set(keyOf(a.fields), a);
    if (typeof a.fields.family === "string") families.set(familyKey(a.fields), a.fields.family);
  }
  // Without vintages (F23) a wine is one without its vintage, unless it is kept by vintage already.
  const noVintages = options.vintages === false;
  const byVintage = new Set<string>();
  if (noVintages) for (const a of existing) if (typeof a.fields.vintage === "number") byVintage.add(keyOf({ ...a.fields, vintage: undefined }));
  const dropVintage = (values: Record<string, unknown>, profile: string, later: string[]) => {
    if (!noVintages || profile !== "wein" || typeof values.vintage !== "number") return;
    if (byVintage.has(keyOf({ ...values, profile, vintage: undefined, size: values.size ?? DEFAULT_SIZE[profile] }))) return;
    later.push(`Jahrgang ${values.vintage} übergangen: Jahrgänge werden nicht geführt`);
    delete values.vintage;
  };
  const places_ = new ImportPlaces(places, options);
  const chosenColors = options.choices?.colors ?? {};
  const rememberedColors = options.remembered?.colors ?? {};
  const colors = new Map<string, UnknownColor>();
  const note = (line: number, message: string, sheet: string) => plan.notes.push({ line, message, ...at(sheet) });
  const error = (line: number, message: string, sheet: string) => plan.errors.push({ line, message, ...at(sheet) });

  // First every row on its own: values, problems, where its stock would go.
  interface Parsed {
    line: number;
    sheet: string;
    /** The profile of its kind, or of its sheet while the kind is open. */
    profile: string;
    key: string;
    values: Record<string, unknown>;
    /** The cells as read, to read them again for the profile a row without kind gets. */
    cells: [string, Cell][];
    stock?: number;
    unitPrice?: number;
    /** The place as written, or undefined for the default place. */
    location?: string;
    /** Notes on cells left out; given once the row is sure to be taken. */
    later: string[];
    /** The colour cell was empty: taken from the known wine, another row, or a decision. */
    colorless?: boolean;
  }
  const parsed: Parsed[] = [];
  for (const sh of read) {
    if (sh.headerAt < 0) continue;
    const columns = sh.rows[sh.headerAt]!.map((c) => columnOf(text(c)));
    // In a workbook a sheet without „Erzeuger“ is no list (the guide, notes): passed over.
    if (many && !columns.includes("producer")) continue;
    sh.rows[sh.headerAt]!.forEach((c, i) => {
      if (columns[i] === null && text(c) !== "" && !plan.ignored.includes(text(c))) plan.ignored.push(text(c));
    });
    const missing = ["producer", "wine", "color"].filter((k) => !columns.includes(k) && !(k === "wine" && sheetProfile(sh.name) === "spirituosen"));
    if (missing.length) {
      for (const k of missing) error(sh.headerAt + 1, `Spalte „${WINE_FIELDS.find((f) => f.key === k)!.label}“ fehlt.`, sh.name);
      continue;
    }
    const sheetDefault = sheetProfile(sh.name) ?? "wein";
    for (let r = sh.headerAt + 1; r < sh.rows.length; r++) {
      const row = sh.rows[r]!;
      const line = r + 1;
      if (row.every((c) => text(c) === "")) continue;
      const values: Record<string, unknown> = {};
      const problems: string[] = [];
      const wrong = new Set<string>();
      // The kind first: it says the profile, and the profile how to read the other cells.
      const ci = columns.indexOf("color");
      const t = ci >= 0 ? text(row[ci]) : "";
      if (t !== "") {
        const found = knownColor(t);
        if (found) values.color = found;
        else {
          const k = norm(t);
          let u = colors.get(k);
          if (!u) {
            const [color, from]: [string | null, ChoiceSource] =
              k in chosenColors ? [chosenColors[k] ?? null, "chosen"] : isColor(rememberedColors[k]) ? [rememberedColors[k]!, "remembered"] : suggestColor(t) ? [suggestColor(t), "suggested"] : [null, "none"];
            u = { value: t, key: k, lines: [], color: isColor(color) ? color : null, from };
            colors.set(k, u);
          }
          u.lines.push(line);
          if (u.color) values.color = u.color;
          else [problems.push(`Farbe „${t}“ keiner Farbe zugeordnet`), wrong.add("color")];
        }
      }
      const profile = (typeof values.color === "string" ? kindProfile(values.color)?.id : undefined) ?? sheetDefault;
      const cells = columns.map((key, i) => [key, row[i]] as [string | null, Cell]).filter((c): c is [string, Cell] => c[0] !== null && c[0] !== "color");
      const later = readCells(cells, profile, values, problems, wrong, rubrics, !noVintages);
      dropVintage(values, profile, later);
      const colorless = values.color === undefined && !wrong.has("color");
      if (values.producer === undefined && !wrong.has("producer")) problems.push(`${WINE_FIELDS.find((f) => f.key === "producer")!.label} fehlt`);
      if (!colorless && values.wine === undefined && !wrong.has("wine") && profileById(profile).fields.find((f) => f.key === "wine")?.required) problems.push("Name fehlt");
      if (problems.length) {
        error(line, [...problems, ...(colorless ? ["Farbe fehlt"] : []), ...later].join("; "), sh.name);
        continue;
      }
      const { stock, location, unitPrice, ...rest } = values as Record<string, unknown> & { stock?: number; location?: string; unitPrice?: number };
      if (!colorless) rest.size ??= DEFAULT_SIZE[profile] ?? 750;
      parsed.push({
        line,
        sheet: sh.name,
        profile,
        key: keyOf({ ...rest, profile, size: rest.size ?? DEFAULT_SIZE[profile] }),
        values: rest,
        cells,
        later,
        ...(stock ? { stock } : {}),
        ...(unitPrice !== undefined ? { unitPrice } : {}),
        ...(location ? { location: String(location) } : {}),
        ...(colorless ? { colorless: true } : {}),
      });
    }
  }

  // A row without a colour: the known wine keeps its colour, another row of the same wine gives
  // one; the rest is grouped by what the name points to and decided once per group.
  const colorOfKey = new Map<string, string>();
  for (const p of parsed) if (!p.colorless && !colorOfKey.has(p.key)) colorOfKey.set(p.key, p.values.color as string);
  const taken: Parsed[] = [];
  for (const p of parsed) {
    if (p.colorless) {
      const existingColor = known.get(p.key)?.fields.color;
      let color = isColor(existingColor) ? existingColor : colorOfKey.get(p.key);
      if (!color) {
        const named = `${p.values.producer} ${p.values.wine ?? ""}`;
        // On a sheet of another profile, its „Sonstiges“ when the name says nothing.
        const sheetKinds = profileById(p.profile).fields.find((f) => f.key === "color")?.options ?? [];
        const hint = colorFromName(named);
        const guess = hint ?? (p.profile !== "wein" ? (sheetKinds.at(-1) ?? null) : null);
        const k = `leer:${guess ?? ""}`;
        let u = colors.get(k);
        if (!u) {
          const chosen = k in chosenColors ? (chosenColors[k] ?? null) : undefined;
          const c = chosen !== undefined ? chosen : guess;
          u = { value: "", key: k, lines: [], color: isColor(c) ? c : null, from: chosen !== undefined ? "chosen" : guess ? "suggested" : "none", empty: { guess, names: [] } };
          colors.set(k, u);
        }
        u.lines.push(p.line);
        u.empty!.names.push(drinkName({ ...p.values, profile: kindProfile(guess ?? "")?.id ?? p.profile }, { vintages: !noVintages }));
        if (!u.color) {
          error(p.line, ["Farbe fehlt", ...p.later].join("; "), p.sheet);
          continue;
        }
        color = u.color;
      }
      p.values.color = color;
      // The kind may say another profile than the sheet: the cells are read again for it.
      const profile = kindProfile(color)?.id ?? p.profile;
      if (profile !== p.profile || p.values.size === undefined) {
        const values: Record<string, unknown> = { color };
        const problems: string[] = [];
        p.later = readCells(p.cells, profile, values, problems, new Set(), rubrics, !noVintages);
        dropVintage(values, profile, p.later);
        if (values.wine === undefined && profileById(profile).fields.find((f) => f.key === "wine")?.required) problems.push("Name fehlt");
        if (problems.length) {
          error(p.line, [...problems, ...p.later].join("; "), p.sheet);
          continue;
        }
        const { stock: _s, location: _l, unitPrice: _u, ...rest } = values;
        rest.size ??= DEFAULT_SIZE[profile] ?? 750;
        p.values = rest;
        p.profile = profile;
        p.key = keyOf({ ...rest, profile });
      }
    }
    for (const message of p.later) note(p.line, message, p.sheet);
    taken.push(p);
    if (p.location && !findPlace(places, p.location)) places_.unknown(p.location, p.line, p.stock ?? 0);
  }
  plan.errors.sort((a, b) => (a.sheet ?? "").localeCompare(b.sheet ?? "") || a.line - b.line);
  // Words first, as they come; then the rows without a colour by the colour proposed, those without a hint last.
  const rank = (c: UnknownColor) => (!c.empty ? -1 : c.empty.guess ? ALL_KINDS.indexOf(c.empty.guess) : ALL_KINDS.length);
  plan.colors = [...colors.values()].sort((a, b) => rank(a) - rank(b));

  // Then each drink once: the same one on several rows is one, its stock is booked per row.
  const groups = new Map<string, Parsed[]>();
  for (const p of taken) groups.set(`${p.profile}|${p.key}`, [...(groups.get(`${p.profile}|${p.key}`) ?? []), p]);
  for (const group of groups.values()) {
    const first = group[0]!;
    const profile = profileById(first.profile);
    const merged = { ...first.values };
    for (const p of group.slice(1)) {
      const differs: string[] = [];
      for (const [k, v] of Object.entries(p.values)) {
        if (merged[k] === undefined) merged[k] = v;
        else if (JSON.stringify(merged[k]) !== JSON.stringify(v)) differs.push(labelOf(k));
      }
      note(p.line, `Derselbe ${profile.id === "wein" ? "Wein" : "Artikel"} wie Zeile ${first.line}${differs.length ? `; ${differs.join(", ")} wie dort` : ""}`, p.sheet);
    }
    const { barcode, ...values } = merged as Record<string, unknown> & { barcode?: string[] };
    const withProfile = profile.id === "wein" ? values : { ...values, profile: profile.id };
    const fields: Record<string, unknown> = { ...values, name: drinkName(withProfile, { vintages: !noVintages }), category: values.color };
    if (barcode) fields.barcodes = barcode;
    const match = known.get(first.key);
    // A still wine without taste gets it from residual sugar and acidity (tasteOf), never over a taste it has.
    if (profile.id === "wein" && fields.taste === undefined && fields.color !== "Schaumwein" && !match?.fields.taste) {
      const taste = tasteOf(fields.sugar ?? match?.fields.sugar, fields.acidity ?? match?.fields.acidity);
      if (taste) fields.taste = taste;
    }
    const ml = sizeOf(values) ?? DEFAULT_SIZE[profile.id] ?? 750;

    const stock: PlannedStock[] = [];
    for (const p of group) {
      if (!p.stock) continue;
      if (match && match.stock !== 0) {
        note(p.line, "hat schon Bestand; die Spalte Bestand gilt nur beim ersten Erfassen", p.sheet);
        continue;
      }
      const target = p.location !== undefined ? places_.target(p.location) : places_.fallback();
      if (!target) {
        note(p.line, p.location !== undefined ? `Lagerort „${p.location}“: Bestand nicht übernommen` : "Kein Lagerort vorhanden, Bestand nicht übernommen", p.sheet);
        continue;
      }
      stock.push({ line: p.line, qty: p.stock, ml, lager: target.lager, location: target.location, unitPrice: p.unitPrice ?? null });
    }

    const where = at(first.sheet);
    if (!match) {
      const family = families.get(familyKey(withProfile)) ?? newId();
      families.set(familyKey(withProfile), family);
      plan.rows.push({
        line: first.line,
        lines: group.map((p) => p.line),
        ...where,
        action: "create",
        id: newId(),
        name: fields.name as string,
        fields: {
          ...profile.defaults,
          ...fields,
          family,
          profile: profile.id,
          unit: "ml",
          tracking: profile.tracking,
          barcodes: fields.barcodes ?? [],
          packs: defaultPacks(profile, ml, typeof values.container === "string" ? values.container : undefined).map((p) => ({ ...p, factor: p.factor * ml })),
        },
        stock,
      });
      continue;
    }
    // Only what the table says differently; an empty cell leaves a field as it is.
    const changed = Object.fromEntries(Object.entries(fields).filter(([k, v]) => JSON.stringify(match.fields[k] ?? null) !== JSON.stringify(v)));
    plan.rows.push({ line: first.line, lines: group.map((p) => p.line), ...where, action: Object.keys(changed).length || stock.length ? "update" : "same", id: match.id, name: fields.name as string, fields: changed, stock });
  }
  planRecipes(recipeSheets, plan, options.recipes ?? [], rubrics, newId, many);
  plan.errors.sort((a, b) => (a.sheet ?? "").localeCompare(b.sheet ?? "") || a.line - b.line);
  plan.notes.sort((a, b) => (a.sheet ?? "").localeCompare(b.sheet ?? "") || a.line - b.line);
  Object.assign(plan, places_.result());
  return plan;
}

/** The sheet of prepared drinks: „Zubereitet“ (also „Zubereitete Getränke“, „Rezepturen“, „Cocktails“). */
export const isRecipeSheet = (name: string) => /zubereit|rezept|cocktail|mixgetr/.test(norm(name));

/** The columns of the sheet „Zubereitet“, in this order. */
export const RECIPE_COLUMNS: readonly Column[] = [
  { key: "listNo", label: "Kartennummer" },
  { key: "wine", label: "Name" },
  { key: "color", label: "Art" },
  { key: "byGlass", label: "Glaspreise" },
  { key: "cardText", label: "Kartentext" },
  { key: "rubric", label: "Rubrik" },
  { key: "offCard", label: "Nicht auf der Karte" },
  { key: "additives", label: "Zusatzstoffe" },
  { key: "allergens", label: "Allergene" },
];

/** A cell of the column „Rubrik“: the title or id of one of the card's rubrics; „-“ or „keine“ takes the drink out of it. */
function rubricValue(t: string, rubrics: readonly Rubric[]): { value?: string | null; error?: string } {
  if (t === "") return {};
  if (/^(-|keine|nein)$/i.test(t)) return { value: null };
  const found = rubrics.find((r) => norm(r.title) === norm(t) || norm(r.id) === norm(t));
  return found ? { value: found.id } : { error: `Rubrik „${t}“ gibt es nicht (${rubrics.map((r) => r.title).join(", ")})` };
}

/**
 * The prepared drinks of the sheet „Zubereitet“ (docs/17-getraenke.md, Die Getränkekarte): one row
 * per drink, recognised by its name, so the same table can be uploaded again. Kind, glass prices,
 * text, rubric, additives and allergens as on the other sheets; an empty cell leaves a field as it is.
 */
function planRecipes(sheets: ImportSheet[], plan: ImportPlan, existing: readonly Recipe[], rubrics: readonly Rubric[], newId: () => string, many: boolean) {
  const known = new Map(existing.map((r) => [fold(r.name), r]));
  const spirits = profileById("spirituosen");
  for (const sh of sheets) {
    const at = many ? { sheet: sh.name } : {};
    const headerAt = sh.rows.findIndex((row) => row.filter((c) => columnOf(text(c)) !== null).length >= 2);
    if (headerAt < 0) continue;
    const columns = sh.rows[headerAt]!.map((c) => columnOf(text(c)));
    if (!columns.includes("wine")) {
      plan.errors.push({ line: headerAt + 1, message: "Spalte „Name“ fehlt.", ...at });
      continue;
    }
    const seen = new Map<string, number>();
    for (let r = headerAt + 1; r < sh.rows.length; r++) {
      const row = sh.rows[r]!;
      const line = r + 1;
      if (row.every((c) => text(c) === "")) continue;
      const cell = (key: string) => (columns.indexOf(key) >= 0 ? row[columns.indexOf(key)] : undefined);
      const name = text(cell("wine"));
      if (!name) {
        plan.errors.push({ line, message: "Name fehlt", ...at });
        continue;
      }
      const kindText = text(cell("color"));
      const kind = kindText === "" ? "Sonstiges" : RECIPE_KINDS.find((k) => norm(k) === norm(kindText));
      if (!kind) {
        plan.errors.push({ line, message: `Art „${kindText}“ unbekannt (${RECIPE_KINDS.join(", ")})`, ...at });
        continue;
      }
      if (seen.has(fold(name))) {
        plan.notes.push({ line, message: `„${name}“ steht schon in Zeile ${seen.get(fold(name))}; nicht übernommen`, ...at });
        continue;
      }
      seen.set(fold(name), line);
      const fields: Record<string, unknown> = { name, kind };
      const later: string[] = [];
      const take = (key: string, field: string, read: (raw: Cell) => { value?: unknown; error?: string }) => {
        const raw = cell(key);
        if (text(raw) === "") return;
        const { value, error } = read(raw);
        if (error) later.push(`${error}; nicht übernommen`);
        else fields[field] = value;
      };
      take("listNo", "listNo", (raw) => valueOf("listNo", raw));
      take("byGlass", "glasses", (raw) => parseGlasses(text(raw)));
      take("cardText", "cardText", (raw) => ({ value: text(raw) }));
      take("rubric", "rubric", (raw) => rubricValue(text(raw), rubrics));
      take("offCard", "offCard", (raw) => {
        const v = valueOf("offCard", raw);
        return v.error ? v : { value: v.value === true };
      });
      take("additives", "additives", (raw) => valueOf("additives", raw, spirits));
      take("allergens", "allergens", (raw) => valueOf("allergens", raw, spirits));
      for (const message of later) plan.notes.push({ line, message, ...at });
      const match = known.get(fold(name));
      if (!match) {
        plan.recipes.push({ line, ...at, action: "create", id: newId(), name, fields: { glasses: [], additives: [], allergens: [], components: [], ...fields } });
        continue;
      }
      const now = match as unknown as Record<string, unknown>;
      const changed = Object.fromEntries(Object.entries(fields).filter(([k, v]) => JSON.stringify(now[k] ?? null) !== JSON.stringify(v ?? null)));
      plan.recipes.push({ line, ...at, action: Object.keys(changed).length ? "update" : "same", id: match.id, name, fields: changed });
    }
  }
}

/**
 * Reads a row's cells (all but the kind) for a profile into `values`. A problem in what makes the
 * drink goes to `problems`, any other to the notes returned. A vintage on a spirit is its age; on a
 * profile without vintage it is left out with a note.
 */
function readCells(cells: [string, Cell][], profileId: string, values: Record<string, unknown>, problems: string[], wrong: Set<string>, rubrics: readonly Rubric[] = DEFAULT_RUBRICS, vintages = true): string[] {
  const profile = profileById(profileId);
  const has = (key: string) => {
    if (EXTRA.some((c) => c.key === key)) return true;
    if (key === "drinkFrom" || key === "drinkTo") return profile.fields.some((f) => f.key === "drink");
    return profile.fields.some((f) => f.key === key);
  };
  const later: string[] = [];
  // The container first: it says how to read the size.
  const containerCell = cells.find(([k]) => k === "container");
  const container = containerCell && has("container") ? valueOf("container", containerCell[1], profile).value : undefined;
  for (const [rawKey, raw] of cells) {
    let key = rawKey;
    if (key === "rubric") {
      const { value, error } = rubricValue(text(raw), rubrics);
      if (error) later.push(`${error}; nicht übernommen`);
      else if (value !== undefined) values.rubric = value;
      continue;
    }
    if (key === "vintage" && profile.id === "spirituosen") key = "age";
    if (!has(key)) {
      if (text(raw) !== "") later.push(`${labelOf(key)} gilt nicht für ${PROFILE_TITLES[profile.id]}; nicht übernommen`);
      continue;
    }
    const { value, error } = valueOf(key, raw, profile, container);
    // Without vintages (F23) a wrong vintage does not make the wine: the cell is left out like any other.
    if (error && IDENTITY.has(key) && (vintages || key !== "vintage")) [problems.push(error), wrong.add(key)];
    else if (error) later.push(key === "stock" ? `${error}; kein Startbestand` : `${error}; nicht übernommen`);
    else if (value !== undefined) values[key] = value;
  }
  // A beer of 30 l without container is a keg.
  const bulk = values.container === undefined && typeof values.size === "number" ? containerFor(profile, values.size, undefined) : undefined;
  if (bulk) values.container = bulk;
  return later;
}

const isColor = (c: unknown): c is string => typeof c === "string" && ALL_KINDS.includes(c);

/** The places of an import: known ones, unknown ones with their decision, and those to create. */
class ImportPlaces {
  private places: Place[];
  private options: ImportOptions;
  private lagers: { id: string; name: string }[];
  private taken: Set<string>;
  private unknowns = new Map<string, UnknownPlace>();
  private newLagers = new Map<string, { id: string; name: string }>();
  private newPlaces = new Map<string, { id: string; lager: string; name: string; label: string }>();
  private defaultPlace: Place | undefined;
  private defaultLager: { id: string; name: string } | undefined;

  constructor(places: Place[], options: ImportOptions) {
    this.places = places;
    this.options = options;
    this.lagers = options.lagers ?? [...new Map(places.map((p) => [p.lager, { id: p.lager, name: p.lagerName }])).values()];
    this.taken = new Set([...(options.taken ?? []), ...places.map((p) => p.id), ...this.lagers.map((l) => l.id)]);
    this.defaultPlace = places.find((p) => /keller/i.test(p.lagerName)) ?? places[0];
    this.defaultLager = this.lagers.find((l) => /keller/i.test(l.name)) ?? this.lagers.find((l) => l.id === this.defaultPlace?.lager) ?? this.lagers[0];
  }

  /** An unknown place in a row: collected once, with its decision. */
  unknown(value: string, line: number, bottles: number) {
    const key = norm(value);
    let u = this.unknowns.get(key);
    if (!u) {
      const chosen = this.options.choices?.places?.[key];
      const remembered = this.options.remembered?.places?.[key];
      const [choice, from]: [PlaceChoice, ChoiceSource] = chosen
        ? [chosen, "chosen"]
        : remembered?.kind === "place" && this.places.some((p) => p.id === remembered.id)
          ? [remembered, "remembered"]
          : [this.suggest(value), "suggested"];
      u = { value, key, lines: [], bottles: 0, parts: splitPlace(value), choice, from, target: null };
      this.unknowns.set(key, u);
    }
    u.lines.push(line);
    u.bottles += bottles;
  }

  private suggest(value: string): PlaceChoice {
    const similar = similarPlace(value, this.places);
    if (similar) return { kind: "place", id: similar.id };
    const parts = splitPlace(value);
    if (parts.lager) {
      const lager = this.lagers.find((l) => norm(l.name) === norm(parts.lager!));
      return lager ? { kind: "new", lager: lager.id, name: parts.name } : { kind: "newLager", lager: parts.lager, name: parts.name };
    }
    return this.defaultLager ? { kind: "new", lager: this.defaultLager.id, name: parts.name } : { kind: "skip" };
  }

  fallback(): PlaceTarget | null {
    const p = this.defaultPlace;
    return p ? { lager: p.lager, location: p.id, label: p.label, created: false } : null;
  }

  /** Where the stock of a row with this place goes; creates what its decision asks for. */
  target(value: string): PlaceTarget | null {
    const known = findPlace(this.places, value);
    if (known) return { lager: known.lager, location: known.id, label: known.label, created: false };
    const u = this.unknowns.get(norm(value))!;
    u.target ??= this.resolve(u.choice);
    return u.target;
  }

  private resolve(choice: PlaceChoice): PlaceTarget | null {
    if (choice.kind === "skip") return null;
    if (choice.kind === "place") {
      const p = this.places.find((x) => x.id === choice.id);
      return p ? { lager: p.lager, location: p.id, label: p.label, created: false } : null;
    }
    const name = choice.name.trim().slice(0, 40);
    if (!name) return null;
    let lager = choice.kind === "new" ? this.lagers.find((l) => l.id === choice.lager) : this.lagers.find((l) => norm(l.name) === norm(choice.lager));
    if (!lager && choice.kind === "newLager") {
      const lagerName = choice.lager.trim().slice(0, 40);
      if (!lagerName) return null;
      lager = this.newLagers.get(norm(lagerName));
      if (!lager) {
        lager = { id: slug(lagerName, this.taken), name: lagerName };
        this.taken.add(lager.id);
        this.newLagers.set(norm(lagerName), lager);
      }
    }
    if (!lager) return null;
    const existing = this.places.find((p) => p.lager === lager.id && norm(p.name) === norm(name));
    if (existing) return { lager: existing.lager, location: existing.id, label: existing.label, created: false };
    const key = `${lager.id}|${norm(name)}`;
    let created = this.newPlaces.get(key);
    if (!created) {
      const inLager = new Set([...this.taken].filter((id) => id.startsWith(`${lager.id}/`)).map((id) => id.slice(lager.id.length + 1)));
      created = { id: `${lager.id}/${slug(name, inLager)}`, lager: lager.id, name, label: `${lager.name} · ${name}` };
      this.taken.add(created.id);
      this.newPlaces.set(key, created);
    }
    return { lager: created.lager, location: created.id, label: created.label, created: true };
  }

  /** Only what stock goes to is created; an unknown place without stock to book keeps its decision and no target. */
  result() {
    return { places: [...this.unknowns.values()], newLagers: [...this.newLagers.values()], newPlaces: [...this.newPlaces.values()] };
  }
}

/** The ways to decide an unknown place, for a selection: new in each storage, a new storage, a known place, or leave the stock out. */
export function placeOptions(u: UnknownPlace, lagers: { id: string; name: string }[], places: Place[]): { key: string; label: string; group: "new" | "known" | "skip"; choice: PlaceChoice }[] {
  const nameIn = (lager: { name: string }) => (u.parts.lager && norm(u.parts.lager) === norm(lager.name) ? u.parts.name : u.value.slice(0, 40));
  const options: { key: string; label: string; group: "new" | "known" | "skip"; choice: PlaceChoice }[] = lagers.map((l) => ({
    key: `new:${l.id}`,
    label: `Neu: ${l.name} · ${nameIn(l)}`,
    group: "new",
    choice: { kind: "new", lager: l.id, name: nameIn(l) },
  }));
  if (u.parts.lager && !lagers.some((l) => norm(l.name) === norm(u.parts.lager!))) {
    options.push({ key: "newLager", label: `Neues Lager: ${u.parts.lager} · ${u.parts.name}`, group: "new", choice: { kind: "newLager", lager: u.parts.lager, name: u.parts.name } });
  }
  for (const p of places) options.push({ key: `place:${p.id}`, label: p.label, group: "known", choice: { kind: "place", id: p.id } });
  options.push({ key: "skip", label: "Bestand nicht übernehmen", group: "skip", choice: { kind: "skip" } });
  return options;
}

/** The option of `placeOptions` a decision stands for. */
export function placeOptionKey(choice: PlaceChoice): string {
  return choice.kind === "new" ? `new:${choice.lager}` : choice.kind === "place" ? `place:${choice.id}` : choice.kind;
}

function findPlace(places: Place[], wanted: string): Place | undefined {
  const w = norm(wanted);
  return (
    places.find((p) => norm(p.label) === w || norm(`${p.lagerName}${p.name}`) === w) ??
    places.find((p) => norm(p.name) === w) ??
    places.find((p) => norm(p.lagerName) === w)
  );
}

/** The catalogue of one profile as rows for its sheet: headings first, then one row per article. */
export function catalogTable(articles: ExistingArticle[], profileId = "wein", rubrics: readonly Rubric[] = DEFAULT_RUBRICS): (string | number | boolean | null)[][] {
  const columns = importColumns(profileId);
  const rows: (string | number | boolean | null)[][] = [columns.map((c) => c.label)];
  const sorted = articles
    .filter((a) => typeof a.fields.producer === "string" && profileOf(a.fields) === profileId)
    .sort((a, b) => String(a.fields.name).localeCompare(String(b.fields.name), "de"));
  for (const a of sorted) {
    const f = a.fields;
    rows.push(
      columns.map(({ key }) => {
        if (key === "stock") return a.stock;
        if (key === "location" || key === "unitPrice") return null;
        if (key === "rubric") return rubrics.find((r) => r.id === f.rubric)?.title ?? null;
        if (key === "barcode") return Array.isArray(f.barcodes) && f.barcodes.length ? f.barcodes.join(", ") : null;
        const v = f[key];
        if (v === undefined || v === null) return null;
        if ((key === "salePrice" || key === "deposit") && typeof v === "number") return v / 100;
        if (key === "size") return (sizeOf(f) ?? 750) / 1000;
        if (key === "alcohol") return typeof v === "number" ? v : formatAlcohol(v) || null;
        if (key === "byGlass") return Array.isArray(v) && v.length ? formatGlasses(v as GlassPrice[]) : null;
        if (key === "allergens") return allergenNames(v).join(", ") || null;
        if (Array.isArray(v)) return v.join(", ");
        if (typeof v === "boolean") return v ? "ja" : null;
        return v as string | number;
      }),
    );
  }
  return rows;
}

/** Example rows for the template, so the format is clear at a glance. */
export const TEMPLATE_EXAMPLES: Cell[][] = [
  [47, "Castello di Ama", "Chianti Classico", "Rot", 2020, 0.75, "Italien", "Toskana", "Chianti Classico DOCG", "Sangiovese, Merlot", 54, "Kirsche, Veilchen und ein Hauch Leder; fest, saftig und lang. Zu Pasta mit Ragù und allem vom Grill.", "1/8 l 7,50; 1/4 l 14,00", null, "trocken", null, 13.5, "Naturkork", 2024, 2032, null, null, null, 12, "Weinkeller · Regal A", 18.5],
  [12, "Dr. Loosen", "Riesling Kabinett", "Weiß", 2022, 0.75, "Deutschland", "Mosel", null, "Riesling", 39, null, null, null, "halbtrocken", null, 8.5, "Schraubverschluss", null, null, null, null, "4013200012345", 24, "Weinkeller · Regal B", 11.9],
];

/** Example rows of the other profiles' sheets, in the order of `importColumns`. Invented names. */
const OTHER_EXAMPLES: Readonly<Record<string, Cell[][]>> = {
  spirituosen: [
    [81, "Brennerei Talgrund", "Williams Christ", "Brand und Obstbrand", null, 0.5, "Deutschland", "Schwarzwald", null, null, "2 cl 4,50; 4 cl 8,00", null, null, 40, null, null, null, null, 2, "Bar · Tagesregal", 24.9],
    [84, "Valle Chiaro", "Amaro", "Bitter und Aperitif", null, 0.7, "Italien", null, null, null, "4 cl 6,00", null, null, "30", "mit Farbstoff", null, null, null, 1, "Bar · Tagesregal", 16.5],
  ],
  bier: [
    [91, "Brauerei Lindenhof", "Pils", "Pils", 0.5, "Flasche", "Deutschland", null, 3.9, null, null, null, null, 4.9, 0.08, null, "Glutenhaltiges Getreide", null, null, 20, "Weinkeller · Regal C", 0.85],
    [90, "Brauerei Lindenhof", "Pils", "Pils", "30 l", "Fass", "Deutschland", null, null, null, "0,3 3,20; 0,5 4,80", null, null, 4.9, 30, null, "Glutenhaltiges Getreide", null, null, 1, "Weinkeller · Regal C", 89],
    [null, "Brauerei Lindenhof", "Pils alkoholfrei", "Pils", 0.33, "Flasche", "Deutschland", null, 3.5, null, null, null, null, "< 0,5", 0.08, null, "Glutenhaltiges Getreide", null, null, 24, "Bar · Kühlschrank", 0.7],
  ],
  alkoholfrei: [
    [null, "Quelle Sonnenberg", "Mineralwasser", "Wasser", "spritzig", 0.75, "Flasche", "Deutschland", null, 4.5, null, null, null, null, null, 0.15, null, null, null, null, 12, "Bar · Kühlschrank", 0.45],
    [null, "Obsthof Lindenhof", "Apfelsaft naturtrüb", "Saft und Nektar", null, 1, "Flasche", "Deutschland", null, null, null, "0,2 2,80; 0,4 4,90", null, null, null, 0.15, null, null, null, null, 6, "Bar · Kühlschrank", 1.6],
  ],
  weitere: [
    [null, "Cidrerie du Verger", "Cidre Brut", "Cidre und Cider", 0.75, "Flasche", "Frankreich", "Normandie", 19, null, null, null, "trocken", null, 4.5, null, null, "Sulfite", null, null, 6, "Weinkeller · Regal C", 4.9],
  ],
};

/** Example rows of the sheet „Zubereitet“, in the order of RECIPE_COLUMNS. */
const RECIPE_EXAMPLES: Cell[][] = [
  [1, "Aperol Spritz", "Aperitif", "0,2 l 7,50", "Aperol, Prosecco, ein Schuss Soda und eine Orangenscheibe.", null, null, "mit Farbstoff", "Sulfite"],
  [null, "Apfelschorle", "Saftschorle", "0,3 l 3,60; 0,5 l 5,20", null, null, null, null, null],
];

/** The sheet „Zubereitet“: headings, then one row per prepared drink. */
export function recipeTable(recipes: readonly Recipe[], rubrics: readonly Rubric[] = DEFAULT_RUBRICS): Cell[][] {
  const rows: Cell[][] = [RECIPE_COLUMNS.map((c) => c.label)];
  for (const r of [...recipes].sort((a, b) => a.name.localeCompare(b.name, "de"))) {
    rows.push([r.listNo, r.name, r.kind, r.glasses.length ? formatGlasses(r.glasses) : null, r.cardText, rubrics.find((x) => x.id === r.rubric)?.title ?? null, r.offCard ? "ja" : null, r.additives.join(", ") || null, allergenNames(r.allergens).join(", ") || null]);
  }
  return rows;
}

/** The template: a sheet per profile with headings and examples, the wines first, then „Zubereitet“ (the guide follows separately). */
export function templateSheets(): ImportSheet[] {
  return [
    ...PROFILES.map((p) => ({ name: PROFILE_TITLES[p.id]!, rows: [importColumns(p.id).map((c) => c.label), ...(p.id === "wein" ? TEMPLATE_EXAMPLES : (OTHER_EXAMPLES[p.id] ?? []))] })),
    { name: "Zubereitet", rows: [RECIPE_COLUMNS.map((c) => c.label), ...RECIPE_EXAMPLES] },
  ];
}

/** The catalogue to take away: the wines' sheet, then a sheet for each profile with articles, then the prepared drinks. */
export function catalogSheets(articles: ExistingArticle[], recipes: readonly Recipe[] = [], rubrics: readonly Rubric[] = DEFAULT_RUBRICS): ImportSheet[] {
  const sheets = PROFILES.map((p) => ({ name: PROFILE_TITLES[p.id]!, rows: catalogTable(articles, p.id, rubrics) as Cell[][] })).filter((sh, i) => i === 0 || sh.rows.length > 1);
  return recipes.length ? [...sheets, { name: "Zubereitet", rows: recipeTable(recipes, rubrics) }] : sheets;
}

export interface ColumnGuide {
  key: string;
  required: boolean;
  /** How to write it. */
  format: string;
  example: string;
  /** What it does. */
  effect: string;
}

/** The second sheet of the template: one row per column, in the order of IMPORT_COLUMNS. */
export const IMPORT_GUIDE: readonly ColumnGuide[] = [
  { key: "listNo", required: false, format: "Zahl oder kurzer Text", example: "47 · 12a", effect: "Nummer auf der Getränkekarte. Das Personal findet den Wein beim Buchen danach („die 47“); auf der Karte ordnet sie die Weine innerhalb einer Gruppe. Im privaten Keller frei nutzbar, etwa für den Platz im Regal; die Kellerliste zeigt sie." },
  { key: "producer", required: true, format: "Text", example: "Weingut Lindenhof", effect: "Weingut, Kellerei oder Handelshaus. Erkennt zusammen mit Name, Jahrgang und Flaschengröße einen Wein wieder: Derselbe Wein wird beim nächsten Hochladen geändert, nicht doppelt angelegt." },
  { key: "wine", required: true, format: "Text", example: "Riesling Kabinett", effect: "Cuvée, Lage oder Linie, ohne Erzeuger und Jahrgang. Teil der Erkennung (siehe Erzeuger)." },
  { key: "color", required: true, format: `eins von: ${WINE_COLORS.join(", ")}`, example: "Weiß", effect: "Gliedert Katalog, Bestand und Getränkekarte (Schaumwein heißt dort „Sekt“). Erkannt werden auch Rotwein, weiss, Sekt, Champagner, Prosecco, Port, Sherry, Dessertwein, Orange Wine und ähnliche Wörter; andere (etwa Weißherbst) ordnet man beim Hochladen einmal einer Farbe zu. Leer: Ein bekannter Wein behält seine Farbe; sonst schlägt die App eine Farbe aus dem Namen vor (Sekt, Secco, Prosecco → Schaumwein; Sherry, Vermut → Likörwein; Limoncello → Likör, Cidre → Cidre und Cider) und fragt einmal je Gruppe. Steht hier die Art eines anderen Profils (Gin, Pils, Wasser), wird die Zeile ein Artikel dieses Profils." },
  { key: "vintage", required: false, format: "Jahr mit vier Ziffern; leer, „o. J.“ oder „NV“ für ohne Jahrgang", example: "2021", effect: "Jeder Jahrgang ist ein eigener Wein mit eigenem Bestand. Teil der Erkennung." },
  { key: "size", required: false, format: `Liter als Zahl (${BOTTLE_SIZES.map((ml) => String(ml / 1000).replace(".", ",")).join(", ")}), auch „75 cl“, „750 ml“, „Magnum“; leer = 0,75`, example: "0,75", effect: "Jede Größe ist ein eigener Wein. Teil der Erkennung; auf der Karte steht sie, wenn sie nicht 0,75 l ist." },
  { key: "country", required: false, format: "Text", example: "Deutschland", effect: "Herkunft; Filter und Gliederung der Karte nach Land." },
  { key: "region", required: false, format: "Text", example: "Mosel", effect: "Herkunft; steht auf der Karte, gliedert die Variante „nach Herkunft“." },
  { key: "appellation", required: false, format: "Text", example: "Chianti Classico DOCG", effect: "Appellation oder Lage; zur Information." },
  { key: "grapes", required: false, format: "Rebsorten, durch Komma getrennt, nach Anteil", example: "Merlot, Cabernet Sauvignon", effect: "Filter und Suche; steht auf der Karte." },
  { key: "salePrice", required: false, format: "Betrag in Euro, mit Komma oder Punkt, mit oder ohne €, auch „39,-“", example: "39,50", effect: "Preis je Flasche auf der Getränkekarte. Ohne Preis steht der Wein nicht auf der Karte." },
  { key: "cardText", required: false, format: "Text, zwei bis drei Sätze", example: "Grüner Apfel, Pfirsich und ein Spritzer Limette …", effect: "Beschreibung für die Gäste auf der Getränkekarte. Gilt für alle Jahrgänge des Weins." },
  { key: "byGlass", required: false, format: "Menge und Preis, mehrere mit Semikolon: 1/8, 1/4, 0,1 l, 0,2 l, 4 cl, 5 cl", example: "1/8 6,50; 1/4 12,50", effect: "Der Wein wird auch offen ausgeschenkt und steht auf der Karte unter „Offene Weine“, eine Spalte je Glasgröße. Leer: nur als Flasche." },
  { key: "offCard", required: false, format: "ja oder nein (auch x, 1 bzw. n, 0, -); leer lässt es, wie es ist", example: "ja", effect: "„ja“ nimmt den Wein von der Getränkekarte, trotz Preis und Bestand (etwa Raritäten nur auf Nachfrage). „nein“ nimmt ihn wieder auf." },
  { key: "taste", required: false, format: "trocken, halbtrocken, lieblich, süß; bei Sekt brut nature bis doux", example: "halbtrocken", effect: "Steht auf der Karte, wenn nicht trocken." },
  { key: "organic", required: false, format: "ja oder leer (auch x, bio)", example: "ja", effect: "Bio-Wein; steht auf der Karte." },
  { key: "alcohol", required: false, format: "Prozent als Zahl, mit oder ohne %", example: "13,5", effect: "Zur Information." },
  { key: "closure", required: false, format: "Naturkork, Schraubverschluss, Glas, Kronkorken, Kunststoff", example: "Naturkork", effect: "Zur Information." },
  { key: "drinkFrom", required: false, format: "Jahr", example: "2024", effect: "Beginn des Trinkfensters." },
  { key: "drinkTo", required: false, format: "Jahr", example: "2032", effect: "Ende des Trinkfensters." },
  { key: "rating", required: false, format: "Text", example: "93 Falstaff", effect: "Bewertung mit Quelle; zur Information." },
  { key: "note", required: false, format: "Text", example: "passt zu Wild", effect: "Interne Notiz, nicht auf der Karte." },
  { key: "barcode", required: false, format: "8 bis 14 Ziffern, mehrere mit Komma", example: "4013200012345", effect: "Strichcode der Flasche: Scannen beim Buchen findet den Wein." },
  { key: "stock", required: false, format: "ganze Zahl Flaschen, auch „6 Fl.“", example: "24", effect: "Nur Startbestand: wird bei einem Wein ohne Bestand als Zugang gebucht. Steht derselbe Wein in mehreren Zeilen (etwa auf zwei Regalen), zählt jede Zeile an ihrem Lagerort. Danach ändern ihn nur Buchungen und Inventur; ein späteres Hochladen ändert ihn nicht." },
  { key: "location", required: false, format: "„Lager · Lagerort“ wie in der App; statt „·“ geht auch „ . “, „/“, „,“ oder „ - “; auch nur Lagerort oder nur Lager", example: "Weinkeller · Regal A", effect: "Wohin der Startbestand gebucht wird. Leer: ins erste Lager mit „Keller“ im Namen. Unbekannt: Die App fragt beim Hochladen, ob er angelegt oder einem vorhandenen zugeordnet wird, auch mit neuem Lager („Garage · Kiste 1“)." },
  { key: "unitPrice", required: false, format: "Betrag in Euro, auch „12,-“", example: "11,90", effect: "Einkaufspreis des Startbestands; Grundlage für den Lagerwert." },
  { key: "rubric", required: false, format: "Titel einer Rubrik der Karte: Aperitif, Longdrinks und Cocktails, Digestif (oder eigene); „-“ nimmt ihn heraus", example: "Digestif", effect: "Das Getränk steht auf der Karte in dieser Rubrik statt in seinem Teil (ein Wein nur mit seinen Glaspreisen), etwa der Grappa unter „Digestif“. Leer lässt es, wie es ist." },
  { key: "quality", required: false, format: "Text, z. B. Qualitätswein, Prädikatswein Kabinett, VDP.Große Lage, DOCG, AOP", example: "Prädikatswein Spätlese", effect: "Qualitätsstufe des Etiketts; steht auf der Karte bei den Angaben." },
  { key: "sugar", required: false, format: "Gramm je Liter, Zahl mit Komma", example: "7,2", effect: "Restzucker aus der Analyse. Ist „Geschmack“ leer, setzt die App ihn bei Stillwein danach (trocken, halbtrocken, lieblich, süß, nach EU-Regel mit der Säure). Auf der Karte in Variante C." },
  { key: "acidity", required: false, format: "Gramm je Liter, Zahl mit Komma", example: "7,8", effect: "Gesamtsäure; zusammen mit dem Restzucker für den Geschmack. Auf der Karte in Variante C." },
  { key: "ph", required: false, format: "Zahl mit Komma", example: "3,2", effect: "pH-Wert; auf der Karte in Variante C." },
  { key: "energy", required: false, format: "kcal je 100 ml, Zahl", example: "72", effect: "Brennwert für das E-Label; zur Information, nicht auf der Karte." },
  { key: "ingredients", required: false, format: "Text", example: "Trauben, Konservierungsstoff: Sulfite", effect: "Zutatenliste für das E-Label; zur Information." },
  { key: "bottler", required: false, format: "Text mit Anschrift", example: "Weinkellerei Muster, 67146 Deidesheim", effect: "Abfüller, wenn nicht der Erzeuger; zur Information." },
  { key: "eLabel", required: false, format: "Internetadresse", example: "https://e-label.example/123", effect: "Adresse hinter dem QR-Code des E-Labels; zur Information." },
  { key: "cooking", required: false, format: "ja oder nein (auch x, 1 bzw. n, 0, -); leer lässt es, wie es ist", example: "ja", effect: "Wein für die Küche, etwa ein einfacher oder einer über dem Trinkfenster; in der App mit dem Filter „zum Kochen“. Für ein Rezept mit Wein schlägt VorratsBuch später nur solche Weine vor. Gilt für diesen Jahrgang und diese Größe; nicht auf der Karte und nicht auf der Kellerliste. „nein“ nimmt die Markierung weg." },
];

/** Columns only the other profiles' sheets have. */
export const OTHER_GUIDE: readonly ColumnGuide[] = [
  { key: "age", required: false, format: "Text", example: "12 Jahre · 2015", effect: "Nur Spirituosen: Alter oder Jahrgang. Teil der Erkennung; steht im Namen." },
  { key: "sparkling", required: false, format: "still, medium oder spritzig", example: "spritzig", effect: "Nur Alkoholfreies, vor allem Wasser; steht im Namen." },
  { key: "container", required: false, format: "Flasche, Dose, Fass, Bag-in-Box, Karton; leer = Flasche", example: "Dose", effect: "0,33 l in der Flasche und in der Dose sind zwei Artikel. Teil der Erkennung." },
  { key: "deposit", required: false, format: "Betrag in Euro je Gebinde", example: "0,08", effect: "Pfand der Flasche, Dose oder des Fasses; zur Information, nicht im Lagerwert." },
  { key: "additives", required: false, format: "durch Komma getrennt, z. B. koffeinhaltig, chininhaltig, mit Farbstoff", example: "koffeinhaltig", effect: "Für die Fußnoten der Getränkekarte (Block 3); in allen Profilen." },
  { key: "allergens", required: false, format: "durch Komma getrennt, z. B. Sulfite, Glutenhaltiges Getreide, Milch", example: "Sulfite", effect: "Neue Weine und Weitere Getränke bekommen Sulfite, Biere Glutenhaltiges Getreide, wenn die Spalte leer ist." },
];

/** The second sheet as rows: headings, then one row per column of the first sheet. */
export function guideTable(): string[][] {
  return [
    ["Spalte", "Pflicht", "Format, wie eingeben", "Beispiel", "Was es bewirkt"],
    ...IMPORT_GUIDE.map((g) => [IMPORT_COLUMNS.find((c) => c.key === g.key)!.label, g.required ? "ja" : "nein", g.format, g.example, g.effect]),
    [],
    ["Allgemein", "", "Eine Zeile je Wein in einem Jahrgang und einer Größe. Die Spalten werden an ihren Überschriften erkannt, die Reihenfolge ist frei; unbekannte Spalten werden übergangen. Ausgelassen wird eine Zeile nur, wenn Erzeuger oder Name fehlen, Jahrgang oder Größe nicht stimmen oder eine Farbe ohne Zuordnung bleibt; ein Fehler in einer anderen Spalte lässt nur diese Zelle aus. Derselbe Wein in mehreren Zeilen wird einmal angelegt, der Bestand jeder Zeile an ihrem Lagerort gebucht. Vor dem Übernehmen zeigt die App, was neu ist und was sich ändert. Danach steht der Import unter „Letzte Importe“, mit den ausgelassenen Zeilen und ihrem Grund.", "", ""],
    ["Weitere Spalten", "", "nur auf den Blättern der anderen Profile", "", ""],
    ...OTHER_GUIDE.map((g) => [PROFILES.flatMap((p) => p.fields).find((f) => f.key === g.key)!.label, "nein", g.format, g.example, g.effect]),
    [],
    ["Blatt „Zubereitet“", "", "Zubereitete Getränke ohne eigenen Bestand: Aperitifs, Longdrinks, Cocktails, Schorlen, Mocktails. Spalten: Kartennummer, Name (Pflicht, erkennt das Getränk wieder), Art (Aperitif, Longdrink, Cocktail, Saftschorle, Weinschorle, Mocktail, Sonstiges), Glaspreise („0,2 l 7,50“, mehrere mit Semikolon), Kartentext, Rubrik, Nicht auf der Karte, Zusatzstoffe, Allergene. Eine Saftschorle und ein Mocktail stehen bei den alkoholfreien Getränken, eine Weinschorle bei den offenen Weinen, die anderen in ihrer Rubrik.", "", ""],
    ["Blätter", "", "Ein Blatt je Profil: Weine, Spirituosen, Biere, Alkoholfreie Getränke, Weitere Getränke. Jedes hat die Spalten seines Profils; „Farbe“ heißt dort „Art“ oder „Stil“ (Gin, Pils, Wasser, Cidre und Cider …). Eine Zeile gehört zum Profil ihrer Art, sonst zu dem ihres Blatts; ein gemischtes Blatt geht also auch. Dazu das Blatt „Zubereitet“ für zubereitete Getränke (siehe oben). Leere Blätter dürfen bleiben, dieses Blatt mit den Erklärungen auch.", "", ""],
  ];
}
